import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * デバッグ用設定の状態
 */
interface DebugState {
  /**
   * HARC API キャッシュを有効にするか。
   * false のとき、HARC 関連の useQuery は staleTime/gcTime を 0 にして
   * キャッシュをバイパスし、永続化（localStorage）対象からも除外される。
   *
   * 既定値は false（ローカルキャッシュ無効）。ローカルキャッシュ起因の不具合を切り分けるため、
   * 当面は既定で無効としている。ON/OFF の切替は ADMIN のみ可能
   * （DebugModal はヘッダーメニューの ADMIN 限定項目からしか開けない）。
   */
  harcCacheEnabled: boolean;
}

/**
 * デバッグ用設定のアクション
 */
interface DebugActions {
  /** HARC キャッシュ有効/無効を設定 */
  setHarcCacheEnabled: (enabled: boolean) => void;
}

/**
 * デバッグ用設定ストア型
 */
export type DebugStore = DebugState & DebugActions;

/**
 * デバッグ用設定ストア
 *
 * 開発・運用上のトラブルシュート用にユーザーが切り替えられる設定を保持する。
 * 値は localStorage に永続化し、ページ再読み込みやセッション跨ぎでも保持される。
 *
 * @example
 * ```tsx
 * const harcCacheEnabled = useDebugStore((s) => s.harcCacheEnabled);
 * ```
 */
export const useDebugStore = create<DebugStore>()(
  persist(
    (set) => ({
      harcCacheEnabled: false,
      setHarcCacheEnabled: (enabled: boolean) => set({ harcCacheEnabled: enabled }),
    }),
    {
      name: 'debug-storage',
      // v0 → v1: ローカルキャッシュ起因の不具合を観察するため、既存の永続値（主に ADMIN が
      // 過去に ON にした値）に関わらず一度 false にリセットする。以降 ADMIN が再度 ON にすれば
      // v1 として保存され、再リセットはされない。
      version: 1,
      partialize: (state) => ({
        harcCacheEnabled: state.harcCacheEnabled,
      }),
      migrate: () => ({ harcCacheEnabled: false }),
    }
  )
);
